import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../../shared/api/types';

type Status = 'connecting' | 'open' | 'closed';

/**
 * WebSocket de chats: se une al canal abierto y entrega cada mensaje nuevo al instante.
 * Si la conexión se cae, reintenta con espera creciente y vuelve a unirse al canal.
 */
export function useChatSocket(churchId: string | null, channelId: string | null, onMessage: (m: ChatMessage) => void): Status {
  const [status, setStatus] = useState<Status>('connecting');
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    if (!churchId || !channelId || typeof WebSocket === 'undefined') return;
    let ws: WebSocket | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const connect = () => {
      setStatus('connecting');
      const url = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/communication/ws`;
      ws = new WebSocket(url);
      ws.onopen = () => {
        retry = 0;
        setStatus('open');
        ws?.send(JSON.stringify({ type: 'join', churchId, channelId }));
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(String(event.data)) as { type: string; message?: ChatMessage };
          if (data.type === 'message' && data.message && data.message.channelId === channelId) handler.current(data.message);
        } catch {
          // Mensaje no reconocido: se ignora.
        }
      };
      ws.onclose = () => {
        setStatus('closed');
        if (stopped) return;
        retry += 1;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(retry, 5)));
      };
    };
    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [churchId, channelId]);

  return status;
}
